import { inject, postConstruct, injectable } from '@theia/core/shared/inversify';
import { ILogger } from "@theia/core";
import { MessageService } from '@theia/core/lib/common';
import { WorkspaceService } from "@theia/workspace/lib/browser/workspace-service";
import URI from '@theia/core/lib/common/uri';

declare global {
    interface Window {
        __userData: {
            entityGuid: string,
            sessionId: string
        };
    }
}

@injectable()
export class IframeListenerService {

    constructor(
        @inject(WorkspaceService) private readonly workspaceService: WorkspaceService,
        @inject(MessageService) private readonly messageService: MessageService,
        @inject(ILogger) private readonly logger: ILogger) {
    }

    @postConstruct()
    protected init(): void {
        window.addEventListener('message', async (event) => await this.handleMessage(event));
        window.dispatchEvent(new CustomEvent('iframe-listener-init'));
        window.parent?.postMessage({ message: 'iframe-listener-init' }, '*');
    }

    private async handleMessage(event: MessageEvent): Promise<void> {
        const payload = event.data || {};
        if (!payload || payload.message !== 'set-frame-parameters') {
            return; // Not our message
        }

        const entityGuid: string = payload.data?.entityGuid?.trim() || '';
        const sessionId: string = payload.data?.sessionId?.trim() || '';

        if (!entityGuid || !sessionId) {
            this.messageService.warn('Missing entityGuid or sessionId in frame parameters.');
            return;
        }

        this.messageService.info(`Cloning repository for ${entityGuid}...`);
        try {
            const postData = {
                entityGuid: entityGuid,
                sessionId: sessionId
            };

            window.__userData = postData;

            const gitCloneResponse = await fetch('./git-init', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(postData)
            });

            const result = await gitCloneResponse.json();
            if (result.ok) {
                // const workspaceRoot = this.workspaceService.tryGetRoots()[0];
                // const rootPath = workspaceRoot?.resource?.path?.toString();

                if (result.newPath) {
                    this.logger.info(`Clone completed: ${result.newPath}`);
                    this.messageService.info(`Clone completed.`);

                    const newUri = new URI(`file://${result.newPath}`);
                    await this.workspaceService.open(newUri, { preserveWindow: true });
                } else {
                    const hash = window.location.hash;
                    const rootPath = hash?.length > 0 ? hash.substring(1) : '';

                    if (result.targetPath && rootPath?.toLowerCase() != result.targetPath.toLowerCase()) {
                        const newUri = new URI(`file://${result.targetPath}`);
                        await this.workspaceService.open(newUri, { preserveWindow: true });
                    } else {
                        this.logger.info(`Repository is up to date: ${result.targetPath}`);
                        this.messageService.info(`Repository is up to date.`);
                    }
                }
            } else {
                this.logger.warn(`Clone failed: ${result?.error ?? String(result)}`);
                this.messageService.error(`Clone failed: ${result?.error ?? String(result)}`);
            }
        } catch (err: any) {
            this.logger.warn(`Clone failed: ${err?.message ?? String(err)}`);
            this.messageService.error(`Clone failed: ${err?.message ?? String(err)}`);
        }
    }
}